/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {HttpEventType} from '@angular/common/http';
import {Component, OnInit, ViewChild} from '@angular/core';
import {ThemePalette} from '@angular/material/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTab, MatTabGroup} from '@angular/material/tabs';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {
  BUSINESS_PLAN,
  DEFAULT_END_YEAR,
  DEFAULT_START_YEAR,
  DISMISS_MSG,
  TVP,
  VEHICLE_COLUMN_HEADERS
} from '../../../common/constants';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {ColumnHeader} from '../../../core/models/column-header.model';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {WorksheetExportRequest} from '../../../core/models/worksheet-export-request.model';
import {AuditGsegResults} from '../../../models/audit-gseg-results.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {VehiclePortfolioLoadResult} from '../../../models/vehicle-portfolio-load-result.model';
import {VehiclePortfolioLoadViolation} from '../../../models/vehicle-portfolio-load-violations.model';
import {VehiclePortfolioLoad} from '../../../models/vehicle-portfolio-load.model';
import {ArchDevianceService} from '../../../services/arch-deviance.service';
import {ExcelExportService} from '../../../services/excel-export.service';
import {VehiclePortfolioLoadService} from '../../../services/vehicle-portfolio-load.service';

// Enum for the tab group for violations
enum VIOLATION_TABS {
  DUPLICATE_ARCH = 'Duplicate Arch per 7key',
  DUPLICATE_ENGCTR = 'Duplicate Engctr per 7key',
  INACTIVE_PLNGMKT = 'Inactive Plngmkt Records',
  INVALID_RECSTAT = 'Invalid Recstat Records',
  INVALID_RECID = 'Duplicate Recids',
  DUPLICATE_ENTRIES = 'Duplicate Entries',
  INVALID_DATE = 'Invalid Date Records',
  INVALID_PORTTYPE = 'Invalid Port Type Records',
  INVALID_ITERATION = 'Invalid Iteration Records'
}

// Enum for the tab group for results
export enum RESULT_TABS {
  NEW_PORTFOLIO = 'New Portfolio',
  OLD_PORTFOLIO = 'Old Portfolio',
  GSEG_AUDIT = 'Gseg Audit',
  REJECTS = 'Rejects',
  CHANGED = 'Changed Records',
  DELETED = 'Deleted Records',
  NEW = 'New Records'
}

@Component({
    selector: 'app-vehicle-portfolio-load',
    templateUrl: './vehicle-portfolio-load.component.html',
    styleUrls: ['./vehicle-portfolio-load.component.scss'],
    standalone: false
})
export class VehiclePortfolioLoadComponent implements OnInit {

  @ViewChild('portfolioTabGroup') portfolioTabGroup: MatTabGroup;
  @ViewChild('violationTabGroup') violationTabGroup: MatTabGroup;
  @ViewChild('resultTabGroup') resultTabGroup: MatTabGroup;

  displayedData: (VehiclePortfolioLoad | AuditGsegResults)[];
  portfolioData: Map<string, VehiclePortfolioLoad[]>;
  allViolations: Map<string, VehiclePortfolioLoadViolation> = new Map<string, VehiclePortfolioLoadViolation>();
  allLoadResults: Map<string, VehiclePortfolioLoadResult> = new Map<string, VehiclePortfolioLoadResult>();
  violations: VehiclePortfolioLoadViolation;
  vehPortLoadResults: VehiclePortfolioLoadResult;

  activeFcorgTab: MatTab;
  portfolioTabs: string[];
  selectedPortType: string;
  currentVehiclePortfolio: VehiclePortfolioLoad[];
  CURRENT_VEH_REPORT = 'Current Vehicle Portfolio';
  BUSINESS_PLAN = BUSINESS_PLAN;
  TVP = TVP;

  vehicleColumnHeaders: ColumnHeader[] = VEHICLE_COLUMN_HEADERS;
  displayedColDef: ColDef[];
  vehicleColDef: ColDef[] = this.vehicleColumnHeaders.map(header => {
    return {field: header.name, headerName: header.label, filter: AutocompleteFilterComponent, lockVisible: true};
  });

  gsegColumnHeaders: ColumnHeader[] = [
    {name: 'sevenKeyFields.plngMkt', label: 'PLNGMKT'},
    {name: 'sevenKeyFields.mktDiv', label: 'BRAND'},
    {name: 'sevenKeyFields.brand', label: 'LBRAND'},
    {name: 'sevenKeyFields.lbrand', label: 'MKTDIV'},
    {name: 'sevenKeyFields.prgmCode', label: 'PRGMCODE'},
    {name: 'sevenKeyFields.styleCode', label: 'BODYSTYLE'},
    {name: 'sevenKeyFields.bodyStyle', label: 'STYLECODE'},
    {name: 'businessPlanGseg', label: 'BP_GSEG'},
    {name: 'tvpGseg', label: 'TVP_GSEG'},
    {name: 'brandfamGseg', label: 'BRANDFAM_GSEG'}
  ];
  gsegColDef: ColDef[] = this.gsegColumnHeaders.map(header => {
    return {field: header.name, headerName: header.label, filter: AutocompleteFilterComponent, lockVisible: true};
  });

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };

  sheets: SelectableOptions = new SelectableOptions();

  hasViolations: boolean;
  hasData: boolean;
  hasResults: boolean;
  isLoadButtonDisabled: boolean;
  showSpinner: boolean;

  pageSize: number;
  fcorgTabIndex: number;
  violationTabIndex: number;
  resultTabIndex: number;

  file: File;
  worksheetExportRequest: WorksheetExportRequest;
  exportButtonColor: ThemePalette = 'accent';

  constructor(public readonly vehPortfolioService: VehiclePortfolioLoadService,
              public readonly archDevianceService: ArchDevianceService,
              public readonly snackBar: FabMessageService,
              public readonly excelService: ExcelExportService,
              private readonly dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.setDefaults();
    this.selectedPortType = BUSINESS_PLAN;
    this.displayedColDef = this.vehicleColDef;
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload() {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets.options,
      isMultiSelect: true
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
      width: '250px'
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheets.selectedOptions = data;
          if (this.sheets.selectedOptions.length > 0) {
            this.getPortfolioData();
          }
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    );
  }

  /**
   * Event handler for when an upload event is emitted. Receives the vehicle portfolio load
   * result data in the event. Displays the results to the user, whether any validation error occurs
   * or any records were successfully updated/added/deleted.
   * @param event The event that is emitted by the Upload button component
   */
  upload(event: any): void {
    this.setDefaults();
    this.sheets.options = event.get('data');
    this.file = event.get('file');
    this.fcorgTabIndex = 0;
    this.onFileUpload();
  }

  /**
   * Get the vehicle portfolio template data from the Excel file that was uploaded
   */
  getPortfolioData(): void {
    this.showSpinner = true;
    this.hasViolations = false;
    this.vehPortfolioService.transformToModel(this.file, this.sheets.selectedOptions).subscribe(
      (e: any) => {
        // Get vehicle portfolio load data from the uploaded Excel file
        if (e.type === HttpEventType.Response) {
          this.portfolioData = e.body;
        }
      },
      (error) => {
        if (error.status === 0) {
          this.snackBar.displayError('Error occurred. Please re-upload file.', DISMISS_MSG);
        } else {
          this.snackBar.displayError(error, DISMISS_MSG);
        }
        this.showSpinner = false;
      },
      () => {
        this.showSpinner = false;
        this.portfolioTabs = Object.keys(this.portfolioData);
        this.hasData = this.portfolioData !== undefined && this.portfolioTabs.length > 0;
        if (this.hasData) {
          this.fcorgTabIndex = 0;
          this.displayedData = this.portfolioData[this.portfolioTabs[this.fcorgTabIndex]];
          this.setExportRequestBody();
          this.isLoadButtonDisabled = false;
          this.setExportButtonColor();
        }
      });
  }

  /**
   * Get the current vehicle portfolio by port type and export to excel
   * @param portType portfolio type
   */
  exportCurrentPortfolioByPortType(portType: string): void {
    this.showSpinner = true;
    this.vehPortfolioService.getCurrentPortfolioByPortType(portType).subscribe(
      (data) => {
        this.currentVehiclePortfolio = data;
      },
      (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.showSpinner = false;
      },
      () => {
        const exportRequest = this.getExportRequestForCurrentPortfolio();
        this.excelService.transformToExcel(exportRequest).subscribe(
          (file: any) => {
            this.excelService.exportToExcelFromServer(file, exportRequest.fileName);
          },
          () => {
            this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
            this.showSpinner = false;
          }, () => {
            this.showSpinner = false;
          });
        this.showSpinner = false;
      });
  }

  /**
   * Helper function used to get the export request for the current vehicle portfolio
   */
  getExportRequestForCurrentPortfolio(): WorksheetExportRequest {
    const excelData = new Map<string, VehiclePortfolioLoad[]>();
    excelData[this.CURRENT_VEH_REPORT] = this.currentVehiclePortfolio;
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    return this.excelService.createExportRequestBody('CurrentVehPort_' + currDate,
      excelData,
      [[this.CURRENT_VEH_REPORT, this.vehicleColumnHeaders]]);
  }

  /**
   * Validate the vehicle portfolio load template, and perform a load
   * if there are no violations
   */
  validate(loadTemplate: Map<string, VehiclePortfolioLoad[]>): void {
    this.showSpinner = true;
    const passesPreCheck = this.performPreCheckValidations(loadTemplate);
    if (passesPreCheck) {
      // Validate the vehicle load template
      this.vehPortfolioService.validatePortfolioLoad(loadTemplate).subscribe(
        (allViolations: Map<string, VehiclePortfolioLoadViolation>) => {
          // Get list of violations from the load template
          this.allViolations = allViolations;
          this.fcorgTabIndex = 0;
          this.violations = allViolations[this.portfolioTabs[this.fcorgTabIndex]];
        },
        (error) => {
          this.snackBar.displayError(error, DISMISS_MSG);
          this.showSpinner = false;
        },
        () => {
          this.showSpinner = false;
          this.violationTabIndex = 0;
          const firstNonEmptyValidationList: string = this.getFirstNonEmptyValidationList();
          this.hasViolations = this.allViolations !== undefined && firstNonEmptyValidationList !== undefined;
          if (this.hasViolations) {
            this.snackBar.displayError('There were violations found in the load template', DISMISS_MSG);
            this.isLoadButtonDisabled = false;
            this.setExportButtonColor();
            this.displayedData = this.violations[firstNonEmptyValidationList];
            this.setExportRequestBody();
          } else {
            // If there are no violations, load the template
            this.snackBar.displayMessage('No violations, performing load.', DISMISS_MSG);
            this.load(loadTemplate);
          }
        });
    }

  }

  /**
   * Load the vehicle portfolio template
   */
  load(loadTemplate: Map<string, VehiclePortfolioLoad[]>): void {
    this.showSpinner = true;
    this.vehPortfolioService.loadPortfolio(loadTemplate).subscribe(
      (allResults: Map<string, VehiclePortfolioLoadResult>) => {
        // Get list of results from doing the load
        this.allLoadResults = allResults;
        this.fcorgTabIndex = 0;
        this.vehPortLoadResults = allResults[this.portfolioTabs[this.fcorgTabIndex]];
      },
      (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.showSpinner = false;
      },
      () => {
        this.resultTabIndex = 0;
        const firstNonEmptyResultList: string = this.getFirstNonEmptyResultList();
        this.hasResults = firstNonEmptyResultList !== undefined;
        this.isLoadButtonDisabled = true;
        this.setExportButtonColor();
        if (this.hasResults) {
          this.displayedData = this.vehPortLoadResults[firstNonEmptyResultList];
          this.setExportRequestBody();
          this.snackBar.displaySuccess('The Vehicle Portfolio was successfully loaded', DISMISS_MSG);
          this.checkForNonComplaintProgramCode();
        } else {
          this.snackBar.displayError('No data was loaded from the portfolio template', DISMISS_MSG);
          this.showSpinner = false;
        }
      });
  }

  /**
   * Set the export request body to be exported to Excel
   */
  setExportRequestBody(): void {
    // Set timeout so that TabGroups do not return as undefined
    setTimeout(() => {
      const currDate = new Date().toISOString();
      if (!this.hasViolations && !this.hasResults) {
        // Export the portfolios
        const portfolioTabs = this.portfolioTabGroup._allTabs.toArray().map(tab => this.createSheetList(tab));
        this.worksheetExportRequest = this.vehPortfolioService.createExportRequestBody(
          'VehiclePortfolio_' + currDate,
          this.portfolioData,
          portfolioTabs);
      } else if (!this.hasViolations && this.hasResults) {
        // Export the results
        const resultTabs = this.resultTabGroup._allTabs.toArray().map(tab => this.createSheetList(tab));
        const data = new Map<string, VehiclePortfolioLoad[] | AuditGsegResults[]>();
        data[RESULT_TABS.NEW_PORTFOLIO] = this.vehPortLoadResults.newPortfolio;
        data[RESULT_TABS.OLD_PORTFOLIO] = this.vehPortLoadResults.oldPortfolio;
        data[RESULT_TABS.GSEG_AUDIT] = this.vehPortLoadResults.gsegAudit;
        data[RESULT_TABS.REJECTS] = this.vehPortLoadResults.rejects;
        data[RESULT_TABS.CHANGED] = this.vehPortLoadResults.changedRecords;
        data[RESULT_TABS.DELETED] = this.vehPortLoadResults.deletedRecords;
        data[RESULT_TABS.NEW] = this.vehPortLoadResults.newRecords;
        this.worksheetExportRequest = this.vehPortfolioService.createExportRequestBody('ResultsVehPortLoad_' + currDate,
          data,
          resultTabs);
        this.exportToExcel();
      } else if (this.hasViolations) {
        // Export the violations
        const violationTabs = this.violationTabGroup._allTabs.toArray().map(tab => this.createSheetList(tab));
        const data = new Map<string, VehiclePortfolioLoad[]>();
        data[VIOLATION_TABS.DUPLICATE_ARCH] = this.violations.multArchPerSevenKeyList;
        data[VIOLATION_TABS.DUPLICATE_ENGCTR] = this.violations.multEngCtrPerSevenKeyList;
        data[VIOLATION_TABS.INACTIVE_PLNGMKT] = this.violations.inactivePlngMktList;
        data[VIOLATION_TABS.INVALID_RECSTAT] = this.violations.invalidRecStatList;
        data[VIOLATION_TABS.INVALID_RECID] = this.violations.duplicateRecIdDtoList;
        data[VIOLATION_TABS.DUPLICATE_ENTRIES] = this.violations.duplicateEntryList;
        data[VIOLATION_TABS.INVALID_DATE] = this.violations.invalidDateList;
        data[VIOLATION_TABS.INVALID_PORTTYPE] = this.violations.invalidPortTypeList;
        data[VIOLATION_TABS.INVALID_ITERATION] = this.violations.invalidIterationList;
        this.worksheetExportRequest = this.vehPortfolioService.createExportRequestBody('ViolationsVehPortLoad_' + currDate,
          data,
          violationTabs);
      }
    });
  }

  /**
   * Gets the name of the first non-empty validation list
   * @return string - name of the first non-empty validation list, undefined if not found
   */
  getFirstNonEmptyValidationList(): string {
    return Object.keys(this.violations).find(key => this.violations[key].length > 0);
  }

  /**
   * Gets the name of the first non-empty result list
   * @return string - name of the first non-empty result list, undefined if not found
   */
  getFirstNonEmptyResultList(): string {
    return Object.keys(this.vehPortLoadResults).find(key => this.vehPortLoadResults[key].length > 0);
  }

  /**
   * Update the datasource of the table data based off the FCORG tab change
   * @param $event The event that is emitted when a tab was changed
   */
  fcorgTabChange($event): void {
    this.activeFcorgTab = $event.tab;
    if (!this.hasViolations && !this.hasResults) {
      this.displayedData = this.portfolioData[this.activeFcorgTab.textLabel];
    } else if (!this.hasViolations && this.hasResults) {
      // If there are result tabs, reset the result tabs back to the first tab
      this.vehPortLoadResults = this.allLoadResults[this.activeFcorgTab.textLabel];
      const getFirstNonEmptyResultList = this.getFirstNonEmptyResultList();
      this.displayedData = getFirstNonEmptyResultList !== undefined ?
        this.vehPortLoadResults[getFirstNonEmptyResultList] : [];
      // Set timeout triggers new angular cycle to reset the tab index
      setTimeout(() => {
        this.resultTabGroup.selectedIndex = 0;
      });
    } else {
      // If there are violations, reset the violation tab group back to the first tab
      this.violations = this.allViolations[this.activeFcorgTab.textLabel];
      const firstNonEmptyValidList: string = this.getFirstNonEmptyValidationList();
      this.displayedData = firstNonEmptyValidList !== undefined ? this.violations[firstNonEmptyValidList] : [];
      // Set timeout triggers new angular cycle to reset the tab index
      setTimeout(() => {
        this.violationTabGroup.selectedIndex = 0;
      });
    }
    this.setExportRequestBody();
  }

  /**
   * Update the datasource of the Violation table data based off the tab change
   * @param $event The event that is emitted when a tab was changed
   */
  violationTabChange($event): void {
    if ($event !== undefined && $event.index !== -1) {
      switch ($event.tab.textLabel) {
        case VIOLATION_TABS.DUPLICATE_ARCH:
          this.displayedData = this.violations.multArchPerSevenKeyList;
          break;
        case VIOLATION_TABS.DUPLICATE_ENGCTR:
          this.displayedData = this.violations.multEngCtrPerSevenKeyList;
          break;
        case VIOLATION_TABS.INACTIVE_PLNGMKT:
          this.displayedData = this.violations.inactivePlngMktList;
          break;
        case VIOLATION_TABS.INVALID_RECSTAT:
          this.displayedData = this.violations.invalidRecStatList;
          break;
        case VIOLATION_TABS.INVALID_RECID:
          this.displayedData = this.violations.duplicateRecIdDtoList;
          break;
        case VIOLATION_TABS.DUPLICATE_ENTRIES:
          this.displayedData = this.violations.duplicateEntryList;
          break;
        case VIOLATION_TABS.INVALID_DATE:
          this.displayedData = this.violations.invalidDateList;
          break;
        case VIOLATION_TABS.INVALID_PORTTYPE:
          this.displayedData = this.violations.invalidPortTypeList;
          break;
        case VIOLATION_TABS.INVALID_ITERATION:
          this.displayedData = this.violations.invalidIterationList;
          break;
        default:
          break;
      }
    }
  }

  /**
   * Update the datasource of the table data based off the result tab changes
   * @param $event The event that is emitted when a tab was changed
   */
  resultTabChange($event): void {
    switch ($event.tab.textLabel) {
      case RESULT_TABS.NEW_PORTFOLIO:
        this.updateDisplayedColumns(this.vehicleColDef);
        this.displayedData = this.vehPortLoadResults.newPortfolio;
        break;
      case RESULT_TABS.OLD_PORTFOLIO:
        this.updateDisplayedColumns(this.vehicleColDef);
        this.displayedData = this.vehPortLoadResults.oldPortfolio;
        break;
      case RESULT_TABS.GSEG_AUDIT:
        this.updateDisplayedColumns(this.gsegColDef);
        this.displayedData = this.vehPortLoadResults.gsegAudit;
        break;
      case RESULT_TABS.REJECTS:
        this.updateDisplayedColumns(this.vehicleColDef);
        this.displayedData = this.vehPortLoadResults.rejects;
        break;
      case RESULT_TABS.CHANGED:
        this.updateDisplayedColumns(this.vehicleColDef);
        this.displayedData = this.vehPortLoadResults.changedRecords;
        break;
      case RESULT_TABS.DELETED:
        this.updateDisplayedColumns(this.vehicleColDef);
        this.displayedData = this.vehPortLoadResults.deletedRecords;
        break;
      case RESULT_TABS.NEW:
        this.updateDisplayedColumns(this.vehicleColDef);
        this.displayedData = this.vehPortLoadResults.newRecords;
        break;
      default:
        break;
    }
  }

  /**
   * Set boolean values used to display components back to their defaults
   */
  setDefaults(): void {
    this.portfolioTabs = [];
    this.fcorgTabIndex = 0;
    this.violationTabIndex = 0;
    this.resultTabIndex = 0;
    this.hasData = false;
    this.isLoadButtonDisabled = true;
    this.setExportButtonColor();
    this.hasViolations = false;
    this.hasResults = false;
  }

  /**
   * Performs pre-check validation steps before back-end validations
   * Current checks: MODDATE must be in yyyymmdd format
   * @param loadTemplate map containing sheet name + sheet data to be loaded
   */
  performPreCheckValidations(loadTemplate: Map<string, VehiclePortfolioLoad[]>): boolean {
    // Currently, only pre-check validation is validating moddates
    return this.validateModDates(loadTemplate);
  }

  /**
   * Helper method to update the columns to display in the table.
   * Every tab will have the same headers, except for the 'Gseg Audit' tab.
   * @param columns Column headers to display
   */
  private updateDisplayedColumns(columns: ColDef[]): void {
    if (this.displayedColDef !== columns) {
      this.displayedColDef = columns;
    }
  }

  /**
   * Helper method to create the SheetList for a WorksheetExportRequest object when exporting to Excel.
   *
   * @param tab MatTab to determine which column headers to use
   */
  private createSheetList(tab: MatTab): [string, ColumnHeader[]] {
    return [
      tab.textLabel,
      tab.textLabel === RESULT_TABS.GSEG_AUDIT ? this.gsegColumnHeaders : this.vehicleColumnHeaders
    ];
  }

  /**
   * Private method to set the color for the export button
   */
  private setExportButtonColor(): void {
    if (this.isLoadButtonDisabled) {
      this.exportButtonColor = 'primary';
    } else {
      this.exportButtonColor = 'accent';
    }
  }

  /**
   * Helper method to validate moddates
   * @param loadTemplate map containing sheet name + sheet data to be loaded
   */
  private validateModDates(loadTemplate: Map<string, VehiclePortfolioLoad[]>): boolean {
    // Create a set of unique moddate values to validate
    const modDates = new Set<string>();
    // Iterate over map and populate set
    Object.keys(loadTemplate).forEach((key) => {
      for (const row of loadTemplate[key]) {
        modDates.add(row.modDate);
      }
    });

    // Validate each moddate
    for (const modDate of modDates) {
      if (this.isValidModDate(modDate) === false) {
        this.showSpinner = false;
        this.snackBar.displayError('Moddates must be valid dates in \'yyyymmdd\' format', DISMISS_MSG);
        return false;
      }
    }

    return true;
  }

  /**
   * Helper method to validate modDate values.
   * Moddates must be empty cells or a valid date in yyyymmdd format
   * @param modDate value to validate
   */
  private isValidModDate(modDate: string): boolean {
    if (modDate.length === 0) {
      return true;
    }
    if (modDate.length !== 8) {
      return false;
    }

    const modDateNumber = Number(modDate) || 0;
    if (modDateNumber === 0) {
      return false;
    }

    // Check if year is greater than default end year or less than default start year
    const year = parseInt(modDate.substring(0, 4), 10);
    if (year > DEFAULT_END_YEAR || year < DEFAULT_START_YEAR) {
      return false;
    }

    const month = parseInt(modDate.substring(4, 6), 10);
    const day = parseInt(modDate.substring(6), 10);
    const date = new Date(`${month}/${day}/${year}`);
    if (date.toDateString() === 'Invalid Date') {
      return false;
    }

    return true;
  }

  /**
   * Check if any non-compliant program code exist where there are architecture deviances
   */
  private checkForNonComplaintProgramCode(): void {
    this.showSpinner = true;
    this.archDevianceService.getNonCompliantProgramCodes().subscribe(data => {
      if (data.length > 0) {
        this.showSpinner = false;
        alert('There are architecture deviances. Please run the Manage Architecture Deviances tool');
      }
    }, (error) => {
      this.showSpinner = false;
      this.snackBar.displayError(error, DISMISS_MSG);
    }, () => {
      this.showSpinner = false;
    });
  }

  /**
   * Export table data to excel
   */
  exportToExcel(): void {
    this.showSpinner = true;
    if (this.worksheetExportRequest === undefined) {
      this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
      this.showSpinner = false;
    } else {
      this.vehPortfolioService.transformToExcel(this.worksheetExportRequest).subscribe(
        (file: any) => {
          this.vehPortfolioService.exportToExcelFromServer(file, this.worksheetExportRequest.fileName);
        },
        () => {
          this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
          this.showSpinner = false;
        }, () => {
          this.showSpinner = false;
        });
    }
  }
}
